import React, { useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import * as moment from 'moment/moment';
import translate from '../../../commonComponents/translate';
import ShopProductVariation from './ShopProductVariation';
import ProductProp, { PACK_TYPE } from '../propTypes/productProp';
import ShopProductImage from './ShopProductImage';
import LoggedUserContext from '../../../contextProviders/LoggedUserContext';
import PriceDisplay from '../../../commonComponents/PriceDisplay';
import ProductSubscription from './ShopProductSubscription';
import {
  FrequencySubscriptionTypes,
  promotionEndingMessage,
} from '../../../services/utilityFunctions';
import ConfigsContext from '../../../contextProviders/ConfigsContext';
import User from '../../../../../../domain/models/user';
import PackVariations from './pack/PackVariations';
import { normalizeNumber } from '../../../services/numberUtilis';
import SocialLinks from '../../../commonComponents/social/SocialLinks';
import { SHARE_LINKS } from '../../../commonComponents/social/propTypes/shareLinkProps';
import AdvancedPack from './pack/AdvancedPack';

function ShopProductContainer({
  t,
  product,
  changeProductQuantity,
  onPackQuantityChange,
  priceTotal,
  minorPriceTotal,
  totalVA,
  totalCV,
  addToCart,
  directSellingActive,
  onBuyNow,
  addToCartLocked,
  productItems,
  packQuantity,
  isMarketPlaceEnabled,
  packVariations,
  handleChangeVariations,
}) {
  const { isPack, pack } = product;
  const isNormalPack = useMemo(
    () => isPack && pack?.packType === PACK_TYPE.NORMAL_PACK,
    [isPack, pack?.packType],
  );

  const isAdvancedPack = useMemo(
    () => isPack && pack?.packType === PACK_TYPE.ADVANCED_PACK,
    [isPack, pack?.packType],
  );

  const { tenant } = useContext(ConfigsContext);
  const { directSellingSettings } = tenant?.settings || {};
  const { CVIntegration, VAIntegration } = directSellingSettings || {};
  const { loggedUser } = useContext(LoggedUserContext);
  const [selectedImage, setSelectedImage] = useState({ id: null, index: 0 });
  const changeImage = id => () => setSelectedImage(id);
  const showVA =
    directSellingActive &&
    VAIntegration &&
    loggedUser?.type === User.TYPE.COLLABORATOR;
  const showCV =
    directSellingActive &&
    CVIntegration &&
    loggedUser?.type === User.TYPE.COLLABORATOR;

  const [subscriptionFrequency, setSubscriptionFrequency] = useState(
    product?.availableSubscriptionFrequency?.length > 0
      ? product?.availableSubscriptionFrequency[0]
      : FrequencySubscriptionTypes.ONLY_ONCE,
  );
  const handleSubscriptionCheckbox = field => {
    setSubscriptionFrequency(field);
  };

  const stockManagementMessage = (
    variation,
    stockInCart,
    isPackProduct = false,
  ) => {
    const stockChosen = isPackProduct
      ? stockInCart * variation.quantity
      : stockInCart;
    // only show msgs when the stock is managed by us, not null
    if (variation?.stockQuantity && variation?.stockQuantity >= 0) {
      const { restockDate, stockQuantity, restockInterval, lowStockWarning } =
        variation || {};
      let message = '';
      let date = null;
      if (restockDate) {
        date =
          new Date().getTime() > new Date(restockDate).getTime()
            ? null
            : moment.utc(restockDate).format('DD MMM, YY');
      }
      if (stockQuantity === 0) {
        const restockIntervalMsg = restockInterval
          ? t('shop.product.warning.message.noStock.interval', restockInterval)
          : t('shop.product.warning.message.noStock');
        message = date
          ? t('shop.product.warning.message.noStock.date', date)
          : restockIntervalMsg;
        return (
          <span
            data-testid="stock-error-first"
            className={
              date || restockInterval ? 'warning-yellow' : 'warning-red'
            }
          >
            {message}
          </span>
        );
      }
      if (variation.stockQuantity < lowStockWarning) {
        message = t('shop.product.warning.message.lowStock');
      }
      if (variation.stockQuantity < stockChosen) {
        const restockIntervalMsg = restockInterval
          ? t(
              'shop.product.warning.message.quantityAboveStock.interval',
              restockInterval,
            )
          : t('shop.product.warning.message.warning.noStock');
        // t('shop.product.warning.message.quantityAboveStock.noDateNoInterval');
        message = date
          ? t('shop.product.warning.message.quantityAboveStock.date', date)
          : restockIntervalMsg;
      }
      return (
        <span
          data-testid="stock-management"
          className={
            !date && !restockInterval ? 'warning-red' : 'warning-yellow'
          }
        >
          {message}
        </span>
      );
    }
    return <span />;
  };

  const userPriceTotal =
    isPack && product?.pack?.userPromotionPrice && priceTotal > 0
      ? {
          originalPriceWithVAT:
            product?.pack?.userPrice?.originalPriceWithVAT * packQuantity,
          originalPriceWithoutVAT:
            product?.pack?.userPrice?.originalPriceWithoutVAT * packQuantity,
          code: product?.variations[0]?.userPrice?.code || 'eur',
          billingType: product?.variations[0]?.userPrice?.billingType,
          highlightedPrice: product?.pack?.userPrice?.highlightedPrice,
        }
      : {
          originalPriceWithVAT: priceTotal * 100,
          originalPriceWithoutVAT: minorPriceTotal * 100,
          code: product?.variations[0]?.userPrice?.code || 'eur',
          billingType: product?.variations[0]?.userPrice?.billingType,
          highlightedPrice: product?.variations[0]?.userPrice?.highlightedPrice,
        };
  const userPromotionPriceTotal =
    isPack && product?.pack?.userPromotionPrice && priceTotal > 0
      ? {
          originalPriceWithVAT: priceTotal * 100,
          originalPriceWithoutVAT: minorPriceTotal * 100,
          code: product?.variations[0]?.userPrice?.code || 'eur',
          billingType: product?.variations[0]?.userPrice?.billingType,
          highlightedPrice: product?.variations[0]?.userPrice?.highlightedPrice,
        }
      : {};

  const linksOptions = useMemo(() => {
    if (loggedUser?._id && isMarketPlaceEnabled) {
      return [{ key: SHARE_LINKS.SHARE_LINK, marketProductId: product._id }];
    }
    return [];
  }, [loggedUser?._id, isMarketPlaceEnabled]);
  return (
    <div className="m-portlet m-portlet--rounded m-portlet--rounded-force m--margin-top-10 m--margin-bottom-10 shop-product">
      <div className="m-portlet__body">
        <div className="row no-padding">
          <div className="col-lg-6 col-md-4">
            <div className="productImageContainer">
              {product.images.length > 1 && (
                <div className="product-thumbnail" id="product-thumbnail">
                  {product.images?.map((image, index) => (
                    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
                    <div
                      onClick={changeImage({ id: image._id, index })}
                      className="product-thumbnail-item"
                      key={`thumb-${image._id}`}
                    >
                      <img
                        src={
                          image?.url ? image.url : '/images/no_image_upload.png'
                        }
                        alt=""
                      />
                    </div>
                  ))}
                </div>
              )}
              <div className="product-img">
                <ShopProductImage
                  product={product}
                  selectedImage={selectedImage.id}
                />
              </div>
            </div>
            <div className="product-share-links">
              <SocialLinks linksOptions={linksOptions} />
            </div>
          </div>
          <div className="col-lg-6 col-md-8">
            <div className="product-title">
              <h2>{product.name}</h2>
            </div>
            {product.sku && (
              <div data-testid={`sku-${product._id}`} className="product-sku">
                {`${t('sku')}: ${product.sku}`}
              </div>
            )}
            {product.summary && (
              <div
                data-testid={`summary-${product._id}`}
                className="product-summary"
              >
                {product.summary}
              </div>
            )}
            <div className="product-info">
              {!isPack &&
                product.variations.map(
                  (v, index) =>
                    v.isPublic && (
                      <ShopProductVariation
                        key={`variation-${v._id}`}
                        index={index}
                        variation={v}
                        t={t}
                        changeProductQuantity={changeProductQuantity}
                        productItems={productItems}
                        stockManagementMessage={stockManagementMessage}
                      />
                    ),
                )}
              {isNormalPack && (
                <PackVariations
                  product={product}
                  stockManagementMessage={stockManagementMessage}
                  index={1}
                  t={t}
                  onPackQuantityChange={onPackQuantityChange}
                  packQuantity={packQuantity}
                />
              )}
              {isAdvancedPack && (
                <AdvancedPack
                  packVariations={packVariations}
                  onChangeVariations={handleChangeVariations}
                  product={product}
                  stockManagementMessage={variation =>
                    stockManagementMessage(
                      variation,
                      packQuantity,
                      isAdvancedPack,
                    )
                  }
                  onPackQuantityChange={onPackQuantityChange}
                  packQuantity={packQuantity}
                />
              )}
              {product.availableForSubscription && (
                <div className="subscription">
                  <ProductSubscription
                    t={t}
                    product={product}
                    subscriptionFrequency={subscriptionFrequency}
                    handleSubscriptionCheckbox={handleSubscriptionCheckbox}
                  />
                </div>
              )}
              <div className="price row">
                <div className="price-totals">
                  {showVA && (
                    <div className="totalVA">{`${t(
                      'shop.product.totalVA',
                    )}: ${normalizeNumber(totalVA, 2)}`}</div>
                  )}
                  {showCV && (
                    <div className="totalCV">{`${t(
                      'shop.product.totalCV',
                    )}: ${normalizeNumber(totalCV, 2)}`}</div>
                  )}
                  {isPack ? (
                    <div className="variation-promotionEnding pack-variation-promotionEnding">
                      {promotionEndingMessage(
                        product?.pack?.promotionPriceDateEnd,
                        'product.promotion.days.2',
                      )}
                    </div>
                  ) : null}
                </div>
                <PriceDisplay
                  t={t}
                  userPrice={userPriceTotal}
                  userPromotionPrice={userPromotionPriceTotal}
                  showFree={false}
                  withWrapper
                />
              </div>
            </div>
            <div className="product-actions row">
              {subscriptionFrequency ===
                FrequencySubscriptionTypes.ONLY_ONCE && (
                <>
                  <span className="col-5">
                    <button
                      type="button"
                      onClick={addToCart}
                      disabled={addToCartLocked}
                      className="btn m-btn--pill btn-theme btn-lg m-btn m-btn--custom cart-button"
                    >
                      <i className="la la-shopping-cart" />
                      <span>{t('shop.addToCart')}</span>
                    </button>
                  </span>
                  <span className="col-7">
                    <a
                      href=""
                      className={`btn m-btn--pill btn-theme btn-lg m-btn m-btn--custom ${
                        addToCartLocked && 'disabled'
                      }`}
                      itemProp="url"
                      onClick={event => onBuyNow(event, null)}
                    >
                      {t('shop.buyNow')}
                    </a>
                  </span>
                </>
              )}
              {subscriptionFrequency !==
                FrequencySubscriptionTypes.ONLY_ONCE && (
                <span className="col-6 text-right">
                  <button
                    type="button"
                    disabled={addToCartLocked}
                    onClick={event => onBuyNow(event, subscriptionFrequency)}
                    className={`btn m-btn--pill btn-theme btn-lg m-btn m-btn--custom ${
                      addToCartLocked && 'disabled'
                    }`}
                  >
                    <span>{t('shop.subscribe')}</span>
                  </button>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

ShopProductContainer.propTypes = {
  t: PropTypes.func.isRequired,
  product: PropTypes.shape(ProductProp.propType).isRequired,
  changeProductQuantity: PropTypes.func.isRequired,
  onPackQuantityChange: PropTypes.func.isRequired,
  addToCart: PropTypes.func.isRequired,
  onBuyNow: PropTypes.func.isRequired,
  priceTotal: PropTypes.number.isRequired,
  minorPriceTotal: PropTypes.number.isRequired,
  totalVA: PropTypes.number.isRequired,
  totalCV: PropTypes.number.isRequired,
  directSellingActive: PropTypes.bool.isRequired,
  addToCartLocked: PropTypes.bool.isRequired,
  productItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      amount: PropTypes.number,
      price: PropTypes.number,
      va: PropTypes.number,
      commissionable: PropTypes.bool,
    }),
  ).isRequired,
  packQuantity: PropTypes.number.isRequired,
  isMarketPlaceEnabled: PropTypes.bool,
  packVariations: PropTypes.arrayOf({
    variation: PropTypes.string,
    quantity: PropTypes.number,
  }),
  handleChangeVariations: PropTypes.func.isRequired,
};

ShopProductContainer.defaultProps = {
  isMarketPlaceEnabled: false,
  packVariations: [],
};

export default translate(ShopProductContainer);
